import React from "react";
import { IonCheckbox, IonIcon } from "@ionic/react";
import clsx from "clsx";

import triangleIcon from "../../assets/icons/triangle_down.svg";
import { TCoachType } from "../../state";
import {
  AI_COACHES,
  AI_COACH_GUIDE_URL,
  AI_COACH_SELECT_DESCRIPTION,
} from "../../constants/coachConstant";
import aiCoachIcon from "../../assets/icons/ai_coach.svg";

import styles from "./CoachSelector.module.scss";

interface CoachSelectorProps {
  selectedCoachTypes: TCoachType[];
  onChange: (coachTypes: TCoachType[]) => void;
}

const CoachSelector: React.FC<CoachSelectorProps> = ({
  selectedCoachTypes,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedCoaches = AI_COACHES.filter(coach =>
    selectedCoachTypes.includes(coach.type),
  );
  const buildDisplayNames = () => {
    if (selectedCoaches.length === 0) return "未選択";
    if (selectedCoaches.length === 1) return selectedCoaches[0].name;
    return `${selectedCoaches[0].name} ...他${selectedCoaches.length - 1}`;
  };
  const displayNames = buildDisplayNames();

  const handleToggleCoach = (coachType: TCoachType) => {
    const nextTypes = selectedCoachTypes.includes(coachType)
      ? selectedCoachTypes.filter(type => type !== coachType)
      : [...selectedCoachTypes, coachType];
    onChange(
      AI_COACHES.map(coach => coach.type).filter(type =>
        nextTypes.includes(type),
      ),
    );
  };

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={styles.bar}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <img src={aiCoachIcon} alt="" className={styles.barIcon} />
        AIコーチを選択
        <span className={styles.selectedNames}>{displayNames}</span>
        <IonIcon
          icon={triangleIcon}
          className={clsx(
            styles.triangleIcon,
            isOpen && styles.triangleIconOpen,
          )}
        />
      </button>
      {isOpen && (
        <div className={styles.card}>
          <a
            className={styles.guideLink}
            href={AI_COACH_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            AIコーチとは？
          </a>
          <p className={styles.description}>{AI_COACH_SELECT_DESCRIPTION}</p>
          <div className={styles.divider} />
          <div className={styles.coachList}>
            {AI_COACHES.map(coach => {
              const checked = selectedCoachTypes.includes(coach.type);
              return (
                <button
                  type="button"
                  key={coach.type}
                  className={styles.coachRow}
                  aria-pressed={checked}
                  onClick={() => handleToggleCoach(coach.type)}
                >
                  <IonCheckbox className={styles.checkbox} checked={checked} />
                  <span
                    className={clsx(
                      styles.coachName,
                      checked && styles.checked,
                    )}
                  >
                    {coach.name}
                  </span>
                </button>
              );
            })}
          </div>
          {selectedCoaches.map(coach => (
            <div key={coach.type} className={styles.feedbackImage}>
              <p className={styles.feedbackTitle}>
                {coach.name}のフィードバックイメージ
              </p>
              <p className={styles.feedbackDescription}>{coach.description}</p>
              <div className={styles.examplesBox}>
                <div className={styles.example}>
                  <p className={styles.exampleLabel}>入力例</p>
                  <p className={styles.exampleText}>{coach.exampleInput}</p>
                </div>
                <div className={styles.example}>
                  <p className={styles.exampleLabel}>出力例</p>
                  <p className={styles.exampleText}>{coach.exampleOutput}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoachSelector;
